import {
  ABILITY_NAMES,
  type AbilityName,
  type AbilityScores,
  type ClassLevelBase,
} from '@character-creator/types';
import { calculateModifier } from './ability-scores';

/** Base proficiency bonus at level 1 */
const BASE_PROFICIENCY_BONUS = 2;
/** Levels between proficiency bonus increases */
const PROFICIENCY_INCREASE_INTERVAL = 4;

/**
 * Calculate proficiency bonus based on total character level
 * Proficiency bonus increases at levels 5, 9, 13, and 17
 */
export function calculateProficiencyBonus(totalLevel: number): number {
  if (totalLevel < 1) return BASE_PROFICIENCY_BONUS;
  return Math.floor((totalLevel - 1) / PROFICIENCY_INCREASE_INTERVAL) + BASE_PROFICIENCY_BONUS;
}

/**
 * Get total character level from all class levels
 */
export function getTotalLevel(classes: ClassLevelBase[]): number {
  return classes.reduce((total, classLevel) => total + classLevel.level, 0);
}

/**
 * Calculate saving throw modifier
 */
export function calculateSavingThrow(
  abilityScore: number,
  proficiencyBonus: number,
  isProficient: boolean
): number {
  const modifier = calculateModifier(abilityScore);
  return isProficient ? modifier + proficiencyBonus : modifier;
}

/**
 * Calculate skill modifier
 */
export function calculateSkillModifier(
  abilityScore: number,
  proficiencyBonus: number,
  proficiencyLevel: 'none' | 'proficient' | 'expertise'
): number {
  const modifier = calculateModifier(abilityScore);

  switch (proficiencyLevel) {
    case 'expertise':
      return modifier + proficiencyBonus * 2;
    case 'proficient':
      return modifier + proficiencyBonus;
    case 'none':
    default:
      return modifier;
  }
}


/**
 * Calculate all saving throw modifiers for a character
 */
export function calculateAllSavingThrows(
  abilityScores: AbilityScores,
  proficiencyBonus: number,
  proficientSaves: AbilityName[]
): Record<AbilityName, number> {
  return ABILITY_NAMES.reduce(
    (result, ability) => {
      result[ability] = calculateSavingThrow(
        abilityScores[ability],
        proficiencyBonus,
        proficientSaves.includes(ability)
      );
      return result;
    },
    {} as Record<AbilityName, number>
  );
}

/**
 * Calculate passive perception
 */
export function calculatePassivePerception(
  wisdomScore: number,
  proficiencyBonus: number,
  isProficient: boolean,
  hasExpertise: boolean
): number {
  const profLevel = hasExpertise ? 'expertise' : isProficient ? 'proficient' : 'none';
  return 10 + calculateSkillModifier(wisdomScore, proficiencyBonus, profLevel);
}
